import React from "react";
import { useNavigate } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { MapPin, Users, Calendar, Gift, Banknote, Clock, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MatchBadge } from "@/components/MatchBadge";

export function CompBadge({ type }) {
  const map = {
    paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-700" },
    gifted: { label: "Gifted", cls: "bg-indigo-100 text-indigo-700" },
    paid_gifted: { label: "Paid + Gifted", cls: "bg-amber-100 text-amber-700" },
  };
  const m = map[type] || map.paid;
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label}</span>;
}

export function OpportunityCard({ opp, index = 0, footer }) {
  const navigate = useNavigate();
  const isEvent = opp.opp_type === "event";
  const spaces = opp.spaces_remaining ?? opp.places_available;
  return (
    <div data-testid={`opportunity-card-${opp.id}`} style={{ animationDelay: `${index * 50}ms` }}
      className={`animate-fade-up overflow-hidden rounded-2xl border bg-white shadow-sm transition-all hover:shadow-lg ${isEvent ? "border-fuchsia-200 ring-1 ring-fuchsia-100" : "border-zinc-200"}`}>
      {opp.cover_image && (
        <div className="relative h-32 w-full overflow-hidden bg-zinc-100">
          <img src={fileUrl(opp.cover_image)} alt="" className="h-full w-full object-cover" />
          {isEvent && <span data-testid={`event-badge-${opp.id}`} className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-fuchsia-600 px-2.5 py-1 text-xs font-bold text-white shadow"><Ticket className="h-3.5 w-3.5" /> EVENT</span>}
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 shrink-0 overflow-hidden rounded-xl bg-zinc-100">
            {opp.business_logo ? <img src={fileUrl(opp.business_logo)} alt="" className="h-full w-full object-cover" />
              : <div className="flex h-full w-full items-center justify-center font-display font-bold text-zinc-400">{opp.business_name?.[0]}</div>}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-zinc-500">{opp.business_name}</p>
            <div className="flex items-center gap-1 text-xs text-zinc-400">
              <MapPin className="h-3 w-3" /> {opp.location}
              {opp.distance_km != null && <span className="text-emerald-600 font-semibold">· {opp.distance_km} km</span>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {isEvent && !opp.cover_image && <span data-testid={`event-badge-${opp.id}`} className="inline-flex items-center gap-1 rounded-full bg-fuchsia-600 px-2 py-0.5 text-xs font-bold text-white"><Ticket className="h-3 w-3" /> EVENT</span>}
            <CompBadge type={opp.compensation_type} />
          </div>
        </div>

        <h3 className="mt-3 font-display text-lg font-bold text-zinc-900">{opp.title}</h3>
        {opp.match && <div className="mt-2"><MatchBadge match={opp.match} testid={`match-badge-${opp.id}`} /></div>}
        <div className="mt-1 flex flex-wrap gap-1.5">
          {opp.category && <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-600">{opp.category}</span>}
          {opp.platforms?.map((p) => <span key={p} className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs capitalize text-zinc-600">{p}</span>)}
        </div>

        {isEvent ? (
          <div className="mt-3 space-y-1.5 text-sm text-zinc-600">
            {opp.venue_name && <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-fuchsia-600" /> {opp.venue_name}</p>}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
              {opp.event_date && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {opp.event_date}</span>}
              {(opp.start_time || opp.end_time) && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {opp.start_time}{opp.end_time ? `–${opp.end_time}` : ""}</span>}
            </div>
            {opp.what_creators_receive && <p className="text-xs text-zinc-600">🎁 {opp.what_creators_receive}</p>}
            {opp.event_status === "cancelled" ? (
              <span className="inline-block rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">Cancelled</span>
            ) : opp.event_status === "completed" ? (
              <span className="inline-block rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-semibold text-zinc-600">Completed</span>
            ) : opp.is_full ? (
              <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">Event full{opp.enable_waitlist ? " · waitlist open" : ""}</span>
            ) : (
              <span data-testid={`event-spaces-${opp.id}`} className="inline-block rounded-full bg-fuchsia-100 px-2 py-0.5 text-xs font-semibold text-fuchsia-700">{spaces} creator {spaces === 1 ? "space" : "spaces"} remaining</span>
            )}
          </div>
        ) : (
          <div className="mt-3 space-y-1.5 text-sm text-zinc-600">
            {opp.deliverables && <p>🎥 {opp.deliverables}</p>}
            <p className="flex items-center gap-1.5">
              {opp.compensation_type !== "gifted" && opp.payment_amount != null && <span className="font-semibold text-emerald-700">£{opp.payment_amount}</span>}
              {opp.compensation_type !== "paid" && opp.gift_description && <span>{opp.compensation_type !== "gifted" ? "+ " : ""}{opp.gift_description}</span>}
            </p>
            <div className="flex items-center gap-4 text-xs text-zinc-500">
              <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {opp.creators_needed} needed</span>
              {opp.deadline && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Apply by {opp.deadline}</span>}
            </div>
          </div>
        )}

        {footer || (
          <div className="mt-4 flex gap-2">
            <Button variant="outline" data-testid={`view-opp-${opp.id}`} onClick={() => navigate(`/opportunities/${opp.id}`)} className="flex-1 rounded-full">{isEvent ? "View Event" : "View"}</Button>
            <Button data-testid={`apply-opp-${opp.id}`} onClick={() => navigate(`/opportunities/${opp.id}`)} className={`flex-1 rounded-full ${isEvent ? "bg-fuchsia-600 hover:bg-fuchsia-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>{isEvent ? "Apply to attend" : "Apply"}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
